import { api, tagTypes } from "./api";


type PaginationParams = {
  page?: number;
  limit?: number;
  search?: string;
  lastLoginRange?: string;
};

// Inject user endpoints into the main API
export const userApi = api.injectEndpoints({
	endpoints: (builder) => ({
		// GET: All users
		getUsers: builder.query<any, PaginationParams>({
		query: ({ page = 1, limit = 10, search ='' , lastLoginRange = ''}) => ({
			url: "user/v1/",
			params: { page, limit, search, lastLoginRange},
		}),
		providesTags: [tagTypes.User],
		}),

		// GET: Stale users
		getStaleUsers: builder.query<any, PaginationParams>({
		query: ({ page = 1, limit = 10, lastLoginRange = '' }) => ({
			url: "user/v1/dashboard/user/stale",
			params: { page, limit, lastLoginRange },
		}),
		providesTags: [tagTypes.User],
		}),


		// DELETE: Remove stale users
		deleteStaleUsers: builder.mutation<any, { userIds: string[] }>({
			query: (payload) => ({
				url: `user/v1/dashboard/user/stale`,
				method: "DELETE",
				body: payload,
			}),
			invalidatesTags: [tagTypes.User],
		}),

		// GET: All verifications
		getUsersVerification: builder.query<any, void>({
			query: () => ({ url: "user/v1/verification", method: "GET" }),
			providesTags: [tagTypes.User],
		}),

		// GET: Single user verification
		getUserVerification: builder.query<any, string>({
			query: (userId) => ({
				url: `user/v1/verification/${userId}`,
				method: "GET",
			}),
			providesTags: [tagTypes.User],
		}),

		// GET: Single user profile
		getUser: builder.query<any, string>({
			query: (userId) => ({ url: `user/v1/profile/${userId}`, method: "GET" }),
			providesTags: [tagTypes.User],
		}),

		// PUT: Update user profile
		updateUser: builder.mutation<any, { userId: string; data: any }>({
			query: ({ userId, data }) => ({
				url: `user/v1/profile/${userId}`,
				method: "PUT",
				body: data,
			}),
			invalidatesTags: [tagTypes.User],
		}),

		// POST: Validate account
		validateAccount: builder.mutation<
			any,
			{ account_number: string; bank_code: string }
		>({
			query: (payload) => ({
				url: "user/v1/admin/wallet/verify-account",
				method: "POST",
				body: payload,
			}),
			invalidatesTags: [tagTypes.Wallet],
		}),

		// POST: Update withdrawal bank
		updateWithdrawalBank: builder.mutation<
			any,
			{
				account_number: string;
				bank_code: string;
				bank_name: string;
				user_id: string;
			}
		>({
			query: (payload) => ({
				url: "user/v1/admin/wallet/bank",
				method: "POST",
				body: payload,
			}),
			invalidatesTags: [tagTypes.Wallet],
		}),

		// GET: Dump customer data
		DumpCustomerData: builder.query<any, void>({
			query: () => ({ url: "user/v1/dashboard/dump-data", method: "GET" }),
			providesTags: [tagTypes.User],
		}),

		// POST: Suspend user
		suspendUser: builder.mutation<any, { userId: string; data: any }>({
			query: ({ userId, data }) => ({
				url: `user/v1/${userId}/suspend`,
				method: "POST",
				body: data,
			}),
			invalidatesTags: [tagTypes.User],
		}),

		// POST: Unsuspend user
		unSuspendUser: builder.mutation<any, { userId: string; data: any }>({
			query: ({ userId, data }) => ({
				url: `user/v1/${userId}/unsuspend`,
				method: "POST",
				body: data,
			}),
			invalidatesTags: [tagTypes.User],
		}),

		// POST: Charge verification fee
		chargeVerificationFee: builder.mutation<any, string>({
			query: (userId) => ({
				url: `user/v1/verification/${userId}/charge-fee`,
				method: "POST",
			}),
			invalidatesTags: [tagTypes.User],
		}),

		// PUT: Delete profile picture
		deleteProfilePic: builder.mutation<any, string>({
			query: (userId) => ({
				url: `user/v1/profile/remove_profile_picture/${userId}`,
				method: "PUT",
			}),
			invalidatesTags: [tagTypes.User],
		}),

		// GET: User feedbacks
		getUserFeedbacks: builder.query<
			any,
			{ page?: number; emojiRating?: string; limit?: number; search?: string;}
		>({
			query: ({ page = 1, emojiRating = "", limit = 10, search = '' } = {}) => ({
				url: `user/v1/dashboard/user/feedback?page=${page}&emojiRating=${emojiRating}&limit=${limit}&search=${search}`,
				method: "GET",
			}),
			providesTags: [tagTypes.User],
		}),

		// GET: Pinned user feedbacks
		getUserPinnedFeedbacks: builder.query<
			any,
			{ page?: number; emojiRating?: string; limit?: number; search?: string }
		>({
			query: ({ page = 1, emojiRating = "", limit = 10, search = '' } = {}) => ({
				url: `user/v1/dashboard/user/pinned-feedback?page=${page}&emojiRating=${emojiRating}&limit=${limit}&search=${search}`,
				method: "GET",
			}),
			providesTags: [tagTypes.User],
		}),

		// PUT: Pin/unpin feedback
		pinUserFeedback: builder.mutation<any, { id: string; pinned: boolean; page?: number; emojiRating?: string; limit?: number; search?: string }>({
			query: ({ id, pinned, page = 1, emojiRating = "", limit = 10, search = '' }) => ({
				url: `user/v1/dashboard/user/pin-feedback/${id}?page=${page}&emojiRating=${emojiRating}&limit=${limit}&search=${search}`,
				method: "PUT",
				body: { pinned },
			}),
			invalidatesTags: [tagTypes.User],
		}),

		// GET: Export user feedback
		exportUserFeedback: builder.query<any, void>({
			query: () => ({ url: "user/v1/dashboard/user", method: "GET" }),
			providesTags: [tagTypes.User],
		}),
	}),
});

export const {
	useGetUsersQuery,
	useGetStaleUsersQuery,
	useDeleteStaleUsersMutation,
	useGetUsersVerificationQuery,
	useGetUserVerificationQuery,
	useGetUserQuery,
	useUpdateUserMutation,
	useValidateAccountMutation,
	useUpdateWithdrawalBankMutation,
	useDumpCustomerDataQuery,
	useLazyDumpCustomerDataQuery,
	useSuspendUserMutation,
	useUnSuspendUserMutation,
	useChargeVerificationFeeMutation,
	useDeleteProfilePicMutation,
	useGetUserFeedbacksQuery,
	useLazyGetUserFeedbacksQuery,
	useGetUserPinnedFeedbacksQuery,
	useLazyGetUserPinnedFeedbacksQuery,
	usePinUserFeedbackMutation,
	useExportUserFeedbackQuery,
	useLazyExportUserFeedbackQuery,
} = userApi;
